import _slicedToArray from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import React, { useRef, useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getSession, stringify } from '@jbrowse/core/util';
import { observer } from 'mobx-react';
import RubberbandSpan from './RubberbandSpan';
import { getRelativeX } from './util';
var useStyles = makeStyles()({
  rubberbandControl: {
    cursor: 'crosshair',
    width: '100%',
    minHeight: 8
  },
  guide: {
    pointerEvents: 'none',
    height: '100%',
    width: 1,
    position: 'absolute',
    zIndex: 10
  }
});
var HoverTooltip = observer(function (_ref) {
  var model = _ref.model,
    open = _ref.open,
    guideX = _ref.guideX,
    overview = _ref.overview;
  var _a;
  var _useStyles = useStyles(),
    classes = _useStyles.classes;
  var cytobandOffset = model.cytobandOffset;
  var _getSession = getSession(model),
    assemblyManager = _getSession.assemblyManager;
  var px = overview.pxToBp(guideX - cytobandOffset);
  var assembly = assemblyManager.get(px.assemblyName);
  var cytoband = (_a = assembly === null || assembly === void 0 ? void 0 : assembly.cytobands) === null || _a === void 0 ? void 0 : _a.find(function (f) {
    return px.coord > f.get('start') && px.coord < f.get('end') && px.refName === assembly.getCanonicalRefName(f.get('refName'));
  });
  return React.createElement(Tooltip, {
    open: open,
    placement: "top",
    title: [stringify(px), cytoband === null || cytoband === void 0 ? void 0 : cytoband.get('name')].join(' '),
    arrow: true
  }, React.createElement("div", {
    className: classes.guide,
    style: {
      left: guideX
    }
  }));
});
function OverviewRubberband(_ref2) {
  var model = _ref2.model,
    overview = _ref2.overview,
    _ref2$ControlComponen = _ref2.ControlComponent,
    ControlComponent = _ref2$ControlComponen === void 0 ? React.createElement("div", null) : _ref2$ControlComponen;
  var cytobandOffset = model.cytobandOffset;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    startX = _useState2[0],
    setStartX = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    currentX = _useState4[0],
    setCurrentX = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    guideX = _useState6[0],
    setGuideX = _useState6[1];
  var controlsRef = useRef(null);
  var _useStyles2 = useStyles(),
    classes = _useStyles2.classes;
  var mouseDragging = startX !== undefined;
  useEffect(function () {
    function globalMouseMove(event) {
      var ref = controlsRef.current;
      if (ref && mouseDragging) {
        setCurrentX(getRelativeX(event, ref));
      }
    }
    function globalMouseUp() {
      // click and drag
      if (startX !== undefined && currentX !== undefined) {
        if (Math.abs(currentX - startX) > 3) {
          var _left = Math.min(startX, currentX);
          var right = Math.max(startX, currentX);
          model.moveTo(overview.pxToBp(_left - cytobandOffset), overview.pxToBp(right - cytobandOffset));
        }
      }
      // just a click
      if (startX !== undefined && currentX === undefined) {
        var click = overview.pxToBp(startX - cytobandOffset);
        if (!click.refName) {
          getSession(model).notify('unknown position clicked');
          console.error('unknown position clicked', click);
        } else {
          model.centerAt(Math.round(click.coord), click.refName, click.index);
        }
      }
      setStartX(undefined);
      setCurrentX(undefined);
      if (startX !== undefined) {
        setGuideX(undefined);
      }
    }
    function globalKeyDown(event) {
      if (event.key === 'Escape') {
        setStartX(undefined);
        setCurrentX(undefined);
      }
    }
    if (mouseDragging) {
      window.addEventListener('mousemove', globalMouseMove, true);
      window.addEventListener('mouseup', globalMouseUp, true);
      window.addEventListener('keydown', globalKeyDown, true);
      return function () {
        window.removeEventListener('mousemove', globalMouseMove, true);
        window.removeEventListener('mouseup', globalMouseUp, true);
        window.removeEventListener('keydown', globalKeyDown, true);
      };
    }
    return function () {};
  }, [mouseDragging, currentX, startX, model, overview, cytobandOffset]);
  function mouseDown(event) {
    event.preventDefault();
    event.stopPropagation();
    setStartX(getRelativeX(event, controlsRef.current));
  }
  function mouseMove(event) {
    setGuideX(getRelativeX(event, controlsRef.current));
  }
  function mouseOut() {
    setGuideX(undefined);
  }
  if (startX === undefined) {
    return React.createElement("div", {
      style: {
        position: 'relative'
      }
    }, guideX !== undefined ? React.createElement(HoverTooltip, {
      model: model,
      open: !mouseDragging,
      overview: overview,
      guideX: guideX
    }) : null, React.createElement("div", {
      className: classes.rubberbandControl,
      role: "presentation",
      ref: controlsRef,
      onMouseDown: mouseDown,
      onMouseOut: mouseOut,
      onMouseMove: mouseMove
    }, ControlComponent));
  }
  var left = startX || 0;
  var width = 0;
  if (startX !== undefined && currentX !== undefined) {
    left = currentX < startX ? currentX : startX;
    width = currentX - startX;
  }
  // calculate the start and end bp of drag
  var leftBpOffset;
  var rightBpOffset;
  if (startX) {
    leftBpOffset = overview.pxToBp(startX - cytobandOffset);
    rightBpOffset = overview.pxToBp(startX + width - cytobandOffset);
    if (currentX && currentX < startX) {
      ;
      var _ref3 = [rightBpOffset, leftBpOffset];
      leftBpOffset = _ref3[0];
      rightBpOffset = _ref3[1];
    }
  }
  return React.createElement("div", {
    style: {
      position: 'relative'
    }
  }, leftBpOffset && rightBpOffset ? React.createElement(RubberbandSpan, {
    leftBpOffset: leftBpOffset,
    rightBpOffset: rightBpOffset,
    width: width,
    left: left
  }) : null, React.createElement("div", {
    "data-testid": "rubberband_controls",
    className: classes.rubberbandControl,
    role: "presentation",
    ref: controlsRef,
    onMouseDown: mouseDown,
    onMouseOut: mouseOut,
    onMouseMove: mouseMove
  }, ControlComponent));
}
export default observer(OverviewRubberband);